import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Heart, LogOut, Menu, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";

const BASE_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/histoire", label: "Notre histoire" },
  { to: "/galerie", label: "Galerie" },
  { to: "/mots", label: "Mots d'amour" },
  { to: "/lettre", label: "Lettre" },
  { to: "/ton-espace", label: "Ton espace" },
];

export default function NavBar() {
  const { isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [...BASE_LINKS];
  if (isAdmin) {
    // Insert Surprise before Letter for admin only
    links.splice(4, 0, { to: "/surprise", label: "Surprise", adminOnly: true });
  }

  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0F0407]/85 border-b border-rose-950/60">
      <nav className="relative flex items-center justify-between px-6 md:px-12 lg:px-16 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-brand">
          <Heart className="w-5 h-5 text-rose-400" fill="currentColor" />
          <span className="font-accent text-2xl text-rose-200">Amor</span>
          {isAdmin && (
            <span data-testid="admin-badge" className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-900/30 border border-amber-700/40 text-amber-200 text-[10px] uppercase tracking-wider">
              <ShieldCheck className="w-3 h-3" /> Admin
            </span>
          )}
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm tracking-wide rounded-full transition ${
                  isActive
                    ? "text-rose-100 bg-rose-900/40"
                    : "text-rose-100/70 hover:text-rose-100 hover:bg-rose-900/20"
                } ${l.adminOnly ? "ring-1 ring-amber-700/40" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex">
          <Button
            data-testid="logout-button"
            onClick={logout}
            variant="ghost"
            className="text-rose-100/80 hover:text-rose-50 hover:bg-rose-900/30"
          >
            <LogOut className="w-4 h-4 md:mr-2" />
            <span className="hidden lg:inline">Se déconnecter</span>
          </Button>
        </div>

        <button
          data-testid="mobile-nav-toggle"
          className="md:hidden text-rose-100"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-rose-950/60 bg-[#0F0407]/95 px-6 py-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg text-sm ${
                  isActive ? "bg-rose-900/40 text-rose-100" : "text-rose-100/80 hover:bg-rose-900/20"
                } ${l.adminOnly ? "border border-amber-700/40" : ""}`
              }
            >
              {l.label}
              {l.adminOnly && <span className="ml-2 text-[10px] text-amber-300">admin</span>}
            </NavLink>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full text-left px-3 py-2 rounded-lg text-sm text-rose-200/80 hover:bg-rose-900/20"
          >
            Se déconnecter
          </button>
        </div>
      )}
    </header>
  );
}
